// Webhooks Events — RIGHT pane (task [3.4], ws-f74mb). Renders the currently-
// selected event: a header (type title + Replay action), the ID/Timestamp/HTTP-
// status/Attempts metadata grid, and Response-body / Message-payload code panels.
//
// MetadataGrid and CodePanel are the components extracted in task [3.1] — reused
// verbatim, not re-implemented. Replay drives useReplayWebhookEvent(); its
// onSuccess invalidates the events query, so both this pane and the list row
// re-render from fresh cache (incremented attempts, refreshed status/timestamp).
import { RotateCw } from 'lucide-react';

import { useReplayWebhookEvent } from '@/api/webhooks/hooks';
import type { WebhookEvent } from '@/api/webhooks/mock';
import {
	CodePanel,
	MetadataGrid,
	type ItemDetailMetadata,
} from '@/components/layout/item-detail-page';
import { Badge } from '@/components/ui/badge';

/** Absolute timestamp for the detail pane (delivery-log detail formats absolute
 *  too; the list pane carries the coarse relative time). */
function formatTimestamp(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString(undefined, {
		month: 'short',
		day: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}

export function EventDetail({ event }: { event: WebhookEvent | null }) {
	const replay = useReplayWebhookEvent();

	if (!event) {
		return (
			<div className="flex items-center justify-center rounded-lg border border-dashed border-border py-20 text-sm text-muted-foreground">
				Select an event to see its delivery detail.
			</div>
		);
	}

	const metadata: ItemDetailMetadata[] = [
		{
			label: 'ID',
			value: <span className="font-mono text-xs">{event.id}</span>,
		},
		{
			label: 'Timestamp',
			value: (
				<span suppressHydrationWarning>{formatTimestamp(event.timestamp)}</span>
			),
		},
		{
			label: 'HTTP Status',
			value: (
				<Badge status={event.status}>{event.httpStatusCode}</Badge>
			),
		},
		{ label: 'Attempts', value: event.attempts },
	];

	return (
		<section className="flex flex-col gap-6 rounded-lg border border-border p-6">
			<header className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						Event
					</p>
					<h2 className="truncate font-mono text-lg font-semibold text-foreground">
						{event.type}
					</h2>
				</div>
				<button
					type="button"
					onClick={() => replay.mutate(event.id)}
					disabled={replay.isPending}
					className="inline-flex shrink-0 items-center gap-1.5 border border-border-strong px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
				>
					<RotateCw
						className={replay.isPending ? 'size-4 animate-spin' : 'size-4'}
						aria-hidden="true"
					/>
					{replay.isPending ? 'Replaying…' : 'Replay'}
				</button>
			</header>

			<MetadataGrid metadata={metadata} />

			<CodePanel heading="Response body" content={event.responseBody} />
			<CodePanel heading="Message payload" content={event.messagePayload} />
		</section>
	);
}
